/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */
import { BaseEventAdapter } from '../BaseEventAdapter/BaseEventAdapter';

// eslint-disable-next-line import/prefer-default-export
export class DatastoreEventAdapter extends BaseEventAdapter {
    constructor(args) {
        super(args);
        if (!args) {
            throw new Error(
                `dto is required when instantiating a new DatastoreEventAdapter object`
            );
        }

        [`datastore`].forEach((name) => {
            if (!args[name]) {
                throw new Error(
                    `${name} property is required when instantiating a new EventService object`
                );
            }
        });

        this.datastore = args.datastore;
    }

    /**
     * Events which have started prior to the 'start' date can be needed, these can be events which cross day
     * boundaries or repeating events with an RRULE which means they should be displayed.
     *
     * filters:document.start<2022-01-12T13:30:00.000Z;document.end>2022-01-12T13:30:00.000Z
     *
     * @param {object} getEventsDto The getEvents dto.
     * @returns {object} getEventsDto A datastore object with correct filters for eventual GET request.
     */
    getPriorStartEvents(getEventsDto) {
        let query = this.datastore.collection(`events`);

        if (getEventsDto && `start` in getEventsDto) {
            query = query.where(`start`, `<`, getEventsDto.start.toISOString());
            query = query.and(`end`, `>`, getEventsDto.start.toISOString());
        }

        return query;
    }

    /**
     * Events which start at some point within the range, these may continue past the range or start and end within it. These can be
     * individual events or repeating events which start within the range.
     *
     * filters:document.start>=2022-01-12T13:30:00.000Z;document.start<2022-01-13T13:30:00.000Z
     *
     * @param {object} getEventsDto The getEvents dto.
     * @returns {object} getEventsDto A datastore object with correct filters for eventual GET request.
     */
    getInRangeStartEvents(getEventsDto) {
        let query = this.datastore.collection(`events`);

        if (getEventsDto && `start` in getEventsDto) {
            // Datastore simulator has a bug with the >= so minus one and use >
            const startDateFix = new Date(getEventsDto.start.getTime() - 1);
            query = query.where(`start`, `>`, startDateFix.toISOString());

            // If there is an end date to the range, use that as the last point in time an event can start to be included
            if (getEventsDto && `end` in getEventsDto) {
                // Datastore simulator has a bug with the <= so add one and use <
                const endDateFix = new Date(getEventsDto.end.getTime() + 1);
                query = query.and(`start`, `<`, endDateFix.toISOString());
            }
        }

        return query;
    }

    /**
     * Get events.
     * @param {object} getEventsDto The getEvents dto.
     * @returns {Promise<object>} The result of the datastore query to get events.
     */
    getEvents(getEventsDto) {
        /*
            Datastore doesn't have anything fancy for events storage, we have to handle things like RRules, past events
            flowing into a day etc. There are 4 circumstances we are interested in for a given date range (start + end date):

            1: Recurring events that start before the date range and end after the start (so within it or after); while not guarantee as the RRule is not parsed its possible it will need to be included.
            2: Simple events that started before the range which end after the start (so within it or after).
            3: Simple and recurring events that start within the range and continue past it.
            4: Simple and recurring events that start and end within the range.

            These can be combined into two queries that need to be made.
            1: Find event that start before the range that have not ended before the range starts
            2: Find events that start after the begin of the range but that do not start past the end of the range
        */
        const priorEventsQuery = this.getPriorStartEvents(getEventsDto);
        const currentRangeEventsQuery =
            this.getInRangeStartEvents(getEventsDto);

        return Promise.all([
            priorEventsQuery.get(),
            currentRangeEventsQuery.get(),
        ]).then((values) => {
            let mergedResults = [];
            values.forEach((results) => {
                // Datastore accepts and returns dates in the ISO string format
                const dataConversion = results.map((e) => {
                    let event = e;

                    // If a data payload exists
                    if (e.data) {
                        event = {
                            // Expand out all the properties into the event data
                            ...JSON.parse(event.data),
                            // But let the base event information override any duplication
                            ...e,
                        };
                    }

                    event.start = new Date(event.start);
                    event.end = new Date(event.end);
                    return event;
                });

                mergedResults = mergedResults.concat(dataConversion);
            });
            return mergedResults;
        });
    }

    /**
     * Get a single event.
     * @param {string} eventId The event datastore document id.
     * @returns {Promise<object>} The result of the datastore query for a single event.
     */
    getEvent(eventId) {
        return this.datastore
            .collection(`events`)
            .doc(eventId)
            .get()
            .then((response) => {
                if (response) {
                    // Datastore accepts and returns dates in the ISO string format
                    const event = response;
                    event.start = new Date(event.start);
                    event.end = new Date(event.end);
                    return event;
                }
                return response;
            });
    }

    /**
     * Count the number of events matching a getEvents query.
     * @param {object} getEventsDto The getEvents dto.
     * @returns {Promise<number>} The number of events.
     */
    countEvents(getEventsDto) {
        const query = this.getEventsQuery(getEventsDto);
        return (
            query
                .count()
                .get()
                // eslint-disable-next-line no-unused-vars
                .then((result) => {
                    // TODO datastore count response handling.
                    // return result.data.
                    return 0;
                })
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.error(error);
                })
        );
    }

    /**
     * Add a new event document.
     * @param {object} event The event object.
     * @returns {Promise<object>} The result of the datastore add query.
     */
    addEvent(event) {
        return this.datastore.collection(`events`).add(event);
    }

    /**
     * Update an event with new values.
     * @param {string} eventId The event document id.
     * @param {object} partialEvent The event data to update.
     * @returns {Promise<object>}The result of the datastore update query.
     */
    updateEvent(eventId, partialEvent) {
        return this.datastore
            .collection(`events`)
            .doc(eventId)
            .update(partialEvent);
    }

    /**
     * Delete a single event.
     * @param {string} eventId The event document id.
     * @returns {Promise<object} The result of the datastore delete query.
     */
    deleteEvent(eventId) {
        return this.datastore.collection(`events`).doc(eventId).delete();
    }
}
